import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useWordPress } from '../context/WordPressContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

export default function Signup() {
  const navigate = useNavigate();
  const { signUp, user, loading: authLoading } = useAuth();
  const { wordpressUrl, isInWordPress } = useWordPress();

  // If user is already authenticated, redirect to dashboard
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    storeName: '',
    storeDomain: '',
    fullName: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Account, 2: Store Details

  // Auto-fill WordPress URL from context
  useEffect(() => {
    if (wordpressUrl && !formData.storeDomain) {
      // Extract domain from WordPress URL (remove protocol)
      const domain = wordpressUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      setFormData(prev => ({ ...prev, storeDomain: domain }));
    }
  }, [wordpressUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(e) {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  }

  async function handleAccountStep(e) {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setStep(2);
  }

  async function handleStoreStep(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation
      if (!formData.storeName || !formData.storeDomain) {
        setError('Store name and domain are required');
        setLoading(false);
        return;
      }

      // 1. Create Supabase Auth user
      const { error: signUpError } = await signUp(
        formData.email,
        formData.password,
        { full_name: formData.fullName }
      );

      if (signUpError) throw signUpError;

      // 2. Register store via backend API
      const registerResponse = await axios.post(`${API_URL}/stores/register`, {
        store_name: formData.storeName,
        store_domain: formData.storeDomain,
        owner_email: formData.email
      });

      const { store } = registerResponse.data;

      // 3. Link user to store
      // Get session for auth token
      const { data: { session } } = await window.supabase.auth.getSession();

      await axios.post(
        `${API_URL}/auth/link-store`,
        { store_id: store.id },
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      // Success! Redirect to connection wizard (NEW: Auto-connect WordPress)
      navigate('/connect', { state: { storeId: store.id, wordpressUrl: wordpressUrl || `https://${formData.storeDomain}` } });

    } catch (err) {
      console.error('Signup error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create account');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex flex-col">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <Sparkles className="w-8 h-8 text-brand-600" />
              <span className="ml-2 text-xl font-bold text-surface-900">Virtual Try-On</span>
            </Link>
            <Link to="/login" className="text-surface-700 hover:text-surface-900">
              Already have an account? <span className="text-brand-600">Login</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Signup Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-card p-8">
            <h2 className="text-3xl font-bold text-center mb-2">Create Your Account</h2>
            <p className="text-surface-600 text-center mb-8">
              {step === 1 ? 'Step 1 of 2: Account Details' : 'Step 2 of 2: Store Details'}
            </p>

            {error && (
              <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded mb-4 flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {step === 1 ? (
              <form onSubmit={handleAccountStep}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-surface-300 rounded-xl focus:ring-brand-500 focus:border-brand-500"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-surface-300 rounded-xl focus:ring-brand-500 focus:border-brand-500"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-surface-300 rounded-xl focus:ring-brand-500 focus:border-brand-500"
                      placeholder="Min. 6 characters"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-surface-300 rounded-xl focus:ring-brand-500 focus:border-brand-500"
                      placeholder="Confirm your password"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-brand-600 text-white py-3 rounded-xl hover:bg-brand-700 font-medium"
                  >
                    Continue
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleStoreStep}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1">
                      Store Name *
                    </label>
                    <input
                      type="text"
                      name="storeName"
                      value={formData.storeName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-surface-300 rounded-xl focus:ring-brand-500 focus:border-brand-500"
                      placeholder="My Fashion Store"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-700 mb-1">
                      WordPress Site URL *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="storeDomain"
                        value={formData.storeDomain}
                        onChange={handleChange}
                        required
                        readOnly={isInWordPress && wordpressUrl}
                        className={`w-full px-4 py-2 border border-surface-300 rounded-xl focus:ring-brand-500 focus:border-brand-500 ${
                          isInWordPress && wordpressUrl ? 'bg-surface-50 cursor-not-allowed' : ''
                        }`}
                        placeholder="myfashionstore.com"
                      />
                      {isInWordPress && wordpressUrl && (
                        <div className="absolute right-3 top-2.5">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-success-100 text-success-700">
                            ✓ Auto-detected
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-surface-500 mt-1">
                      {isInWordPress && wordpressUrl
                        ? 'Automatically detected from your WordPress site'
                        : 'Enter your WooCommerce store URL'
                      }
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 bg-surface-200 text-surface-700 py-3 rounded-xl hover:bg-surface-300 font-medium"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-brand-600 text-white py-3 rounded-xl hover:bg-brand-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating Account...' : 'Create Account'}
                    </button>
                  </div>
                </div>
              </form>
            )}

            <p className="text-center text-sm text-surface-600 mt-6">
              By signing up, you agree to our{' '}
              <button type="button" className="text-brand-600 hover:underline">Terms of Service</button>
              {' '}and{' '}
              <button type="button" className="text-brand-600 hover:underline">Privacy Policy</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
